import React, { memo, useCallback, useState } from 'react'

import {
  Icon,
  Button,
  RotatingSpinner,
  Trash,
  ShareOutline,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useDeleteModelSession from 'Pods/ThreeDStudio/Hooks/useDeleteModelSession'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import DccBridgeButton from './Components/DccBridge/DccBridgeButton/DccBridgeButton'
import SendToButton from './Components/DccBridge/SendToButton/SendToButton'
import SendToModal from './Components/DccBridge/SendToModal/SendToModal'
import ExportButton from './Components/Export/ExportButton/ExportButton'
import ExportFormatButton from './Components/Export/ExportFormatButton/ExportFormatButton'
import ModelTitle from './Components/ModelTitle/ModelTitle'

const deleteIcon = <Icon svgComponent={Trash} color="darker" size={iconSize} notSpaced />
const shareIcon = <Icon svgComponent={ShareOutline} color="darker" size={iconSize} notSpaced />
const loadingShareIcon = <Icon svgComponent={RotatingSpinner} color="darker" size={iconSize} notSpaced />

let ShareModal
let ThreeDStudioShareContent

function FooterActions() {
  const generatorSessionId = useThreeDStudioStore(state => state.generation?.generatorSessionId)
  const modelTitle = useThreeDStudioStore(
    state => state.ui?.openedModel?.title ?? state.ui?.openedModel?.prompt,
  )
  const runId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const isReadOnly = useOpenedModelReadOnly()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isShareModalLoading, setIsShareModalLoading] = useState(false)
  const [isShareModalLoaded, setIsShareModalLoaded] = useState(() => !!ShareModal)

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const { deleteModel, isLoading: isDeleting } = useDeleteModelSession({ onSuccess: handleCloseDeleteModal })

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    deleteModel(generatorSessionId)
  }, [deleteModel, generatorSessionId])

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false)
  }, [])

  const importShareModal = useCallback(() => {
    setIsShareModalLoading(true)
    Promise.all([
      import('Pods/LazyLoaded/ShareModal/ShareModal'),
      import('Pods/ThreeDStudio/Shared/ShareContent/ThreeDStudioShareContent'),
    ])
      .then(([shareModalModule, shareContentModule]) => {
        ShareModal = shareModalModule.default
        ThreeDStudioShareContent = shareContentModule.default
        setIsShareModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load ShareModal:', error)
      })
      .finally(() => {
        setIsShareModalLoading(false)
      })
  }, [])

  const handleShareClick = useCallback(() => {
    if (!runId) return
    setIsShareModalOpen(true)
    if (!ShareModal && !isShareModalLoaded && !isShareModalLoading) {
      importShareModal()
    }
  }, [runId, isShareModalLoaded, isShareModalLoading, importShareModal])

  const isShareDisabled = !runId || isShareModalLoading

  // Owner-only actions are hidden when viewing someone else's model; the
  // backend would reject delete/title/settings mutations anyway.
  if (isReadOnly) {
    return (
      <div className="footer-actions">
        <ModelTitle />
      </div>
    )
  }

  return (
    <div className="footer-actions">
      <ModelTitle />
      <div className="footer-actions__actions">
        <Button
          icon={deleteIcon}
          type="secondary"
          size="small"
          variant="only-icon"
          aria-label="Delete"
          customClass="footer-actions__delete"
          onClick={handleOpenDeleteModal}
          disabled={isDeleting}
        />
        <Button
          icon={isShareModalLoading ? loadingShareIcon : shareIcon}
          type="secondary"
          size="small"
          variant="only-icon"
          aria-label="Share"
          onClick={handleShareClick}
          disabled={isShareDisabled}
        />
        <div className="dcc-bridge-cluster">
          <SendToButton />
          <DccBridgeButton />
        </div>

        <div className="export-cluster">
          <ExportButton />
          <ExportFormatButton />
        </div>
      </div>
      <SendToModal />
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="model"
          itemName={modelTitle}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDeleteModal}
        />
      )}
      {isShareModalOpen && isShareModalLoaded && ShareModal && ThreeDStudioShareContent && (
        <ShareModal
          closeModal={handleCloseShareModal}
          content={<ThreeDStudioShareContent />}
        />
      )}
    </div>
  )
}

export default memo(FooterActions)
